import { Link } from "react-router-dom";
import {
  Phone,
  ShieldAlert,
  Check,
  X,
  ArrowRight,
  ScrollText,
  Lock,
  Eye,
  HandCoins,
  AlertTriangle,
} from "lucide-react";
import { COMPANY, ARTISAN_BADGE } from "../lib/constants";
import useSeo, { breadcrumbJsonLd } from "../lib/useSeo";

const TIPS = [
  {
    icon: AlertTriangle,
    title: "Méfiez-vous des numéros dans des dépliants ou Google Ads anonymes",
    desc: "Les arnaqueurs paient pour apparaître en haut de Google. Vérifiez toujours l'adresse, le SIRET, la fiche Google avec avis, et la présence d'un site sérieux et localisé.",
  },
  {
    icon: Eye,
    title: "Demandez le tarif AVANT le déplacement",
    desc: "Tout serrurier honnête vous annonce un fourchette de prix au téléphone et la confirme par écrit avant de commencer. Refusez toute intervention sans tarif clair.",
  },
  {
    icon: Lock,
    title: "Refusez le perçage automatique de la serrure",
    desc: "Un vrai serrurier sait ouvrir 9 portes claquées sur 10 sans dégât (crochetage, technique manuelle). Un faux serrurier perce systématiquement pour vendre une serrure neuve.",
  },
  {
    icon: HandCoins,
    title: "Ne signez jamais une facture en blanc",
    desc: "La facture doit détailler chaque ligne (déplacement, main d'œuvre, pièces). Aucun paiement obligatoire avant lecture complète. Conservez votre exemplaire.",
  },
];

const COMMITMENTS = [
  "Tarifs annoncés AVANT le déplacement et CONFIRMÉS avant intervention",
  "Devis gratuit, écrit, sans engagement",
  "Ouverture fine privilégiée — préservation de votre serrure",
  "Aucun perçage sans accord explicite signé",
  "Facture détaillée acceptée par toutes les assurances",
  "Prix conformes à la grille publique affichée sur ce site",
  "Aucun intermédiaire, aucune sous-traitance — vous parlez directement à votre artisan",
  "Matériel certifié des grandes marques (Picard, Vachette, Bricard, Héraclès, Assa Abloy)",
  "Disponibilité réelle 24h/24, 7j/7 — sans surfacturation injustifiée",
  "Possibilité d'attendre le lendemain si non-urgence pour éviter les majorations",
];

const COMPARE_HEAD = ["Mon Serrurier Breton", "Faux serruriers / réseaux"];
const COMPARE_ROWS = [
  ["Tarif annoncé au téléphone", "Refus de donner un prix avant venue"],
  ["Forfait déplacement 50 €", "Frais cachés multipliés sur place"],
  ["Ouverture fine en priorité", "Perçage systématique pour vendre une serrure"],
  ["Facture détaillée et signée", "Pression à payer avant lecture"],
  ["Vrais avis Google vérifiables", "Aucun avis ou avis peu crédibles"],
  ["Adresse + SIRET visibles", "Adresse fictive ou inexistante"],
  ["Artisan local, pas d'intermédiaire", "Centre d'appels, sous-traitance"],
];

export default function Charte() {
  useSeo({
    title:
      "Charte anti-arnaque serrurier — Tarifs publics & engagements | Mon Serrurier Breton",
    description:
      "Notre engagement contre les arnaques de serrurerie : tarifs publics annoncés avant intervention, ouverture sans casse, facture détaillée pour assurance. Découvrez nos 10 engagements et nos conseils pour ne jamais vous faire piéger.",
    path: "/charte-transparence",
    jsonLd: [
      breadcrumbJsonLd([
        { name: "Accueil", path: "/" },
        { name: "Charte de transparence", path: "/charte-transparence" },
      ]),
    ],
  });
  return (
    <div data-testid="page-charte">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(44,168,182,0.3),transparent_55%)]" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-400/40 text-orange-200 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-4">
            <ShieldAlert className="w-3.5 h-3.5" /> Charte anti-arnaque
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95] max-w-3xl">
            Notre engagement contre
            <br />
            les <span className="text-amber-300">arnaques de serrurerie.</span>
          </h1>
          <p className="text-teal-50/90 mt-6 text-lg max-w-2xl leading-relaxed">
            Le secteur de la serrurerie d'urgence est gangrené par des
            arnaqueurs qui peuvent transformer une porte claquée à 100 € en
            facture de 1 500 €. Voici nos engagements concrets et nos conseils
            pour ne jamais vous faire piéger — chez nous comme ailleurs.
          </p>
          <div className="mt-8 inline-flex items-center gap-4 bg-white/10 border border-white/20 rounded-2xl px-5 py-3 backdrop-blur">
            <img
              src={ARTISAN_BADGE}
              alt="Certification Artisan"
              className="h-14 w-auto object-contain"
              data-testid="charte-artisan-badge"
            />
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-widest text-teal-300 font-bold">
                Certification
              </div>
              <div className="font-display font-extrabold text-white">
                Artisan reconnu
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Engagements */}
      <section
        data-testid="charte-engagements"
        className="max-w-6xl mx-auto px-4 md:px-8 py-20"
      >
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <ScrollText className="w-4 h-4" /> Nos 10 engagements
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Ce que vous pouvez
            <br />
            <span className="text-teal-600">exiger de nous.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COMMITMENTS.map((c, i) => (
            <div
              key={i}
              data-testid={`commitment-${i + 1}`}
              className="flex items-start gap-4 bg-white border border-slate-200 rounded-3xl p-6"
            >
              <div className="w-10 h-10 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center flex-shrink-0">
                <Check className="w-5 h-5" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-teal-700 mb-1">
                  Engagement {String(i + 1).padStart(2, "0")}
                </div>
                <p className="text-slate-800 leading-relaxed">{c}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Comparaison */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-20">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Repérez la différence
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Vrai serrurier <span className="text-teal-600">vs</span> arnaque.
          </h2>
        </div>
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <div className="grid grid-cols-2 bg-slate-900 text-white">
            <div className="p-5 md:p-6 font-display font-extrabold flex items-center gap-2 border-r border-white/10">
              <Check className="w-4 h-4 text-teal-300" /> {COMPARE_HEAD[0]}
            </div>
            <div className="p-5 md:p-6 font-display font-extrabold flex items-center gap-2 text-orange-200">
              <X className="w-4 h-4" /> {COMPARE_HEAD[1]}
            </div>
          </div>
          {COMPARE_ROWS.map((row, i) => (
            <div
              key={i}
              data-testid={`compare-row-${i + 1}`}
              className={`grid grid-cols-2 ${i % 2 ? "bg-slate-50" : "bg-white"}`}
            >
              <div className="p-4 md:p-5 border-r border-slate-200 flex items-start gap-2 text-slate-800">
                <Check className="w-4 h-4 text-teal-600 mt-1 flex-shrink-0" />
                <span>{row[0]}</span>
              </div>
              <div className="p-4 md:p-5 flex items-start gap-2 text-slate-500 line-through-soft">
                <X className="w-4 h-4 text-orange-500 mt-1 flex-shrink-0" />
                <span>{row[1]}</span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Tips */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-20">
        <div className="max-w-2xl mb-10">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> 4 conseils essentiels
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Comment éviter
            <br />
            <span className="text-teal-600">de vous faire piéger.</span>
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {TIPS.map((t, i) => (
            <div
              key={i}
              data-testid={`tip-${i + 1}`}
              className="bg-white border border-slate-200 rounded-3xl p-7"
            >
              <div className="w-12 h-12 rounded-2xl bg-orange-50 text-orange-700 flex items-center justify-center mb-4">
                <t.icon className="w-6 h-6" />
              </div>
              <div className="text-xs font-bold uppercase tracking-widest text-orange-700 mb-1">
                Conseil {String(i + 1).padStart(2, "0")}
              </div>
              <h3 className="font-display text-lg md:text-xl font-extrabold text-slate-900 mb-2">
                {t.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 md:px-8 pb-20">
        <div className="rounded-3xl bg-gradient-to-br from-teal-700 to-slate-900 text-white p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight leading-tight">
                Un artisan, pas un piège.
              </h2>
              <p className="text-teal-100 mt-3 max-w-md">
                Tarifs publiés, devis annoncé, intervention soignée. Voilà le
                vrai métier.
              </p>
            </div>
            <div className="flex md:justify-end flex-wrap gap-3">
              <a
                data-testid="charte-cta-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-7 py-4 shadow-xl text-lg"
              >
                <Phone className="w-5 h-5" /> {COMPANY.phone}
              </a>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-white text-slate-900 font-bold rounded-full px-6 py-4"
              >
                Voir nos tarifs <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
